import z from 'zod';

/**
 * Environment variable schema definition.
 * Following TanStack Start environment variables guide:
 * https://tanstack.com/start/latest/docs/framework/react/guide/environment-variables
 */

// Helper to convert string "true"/"false" to boolean, defaulting to true if undefined
const booleanString = () =>
  z
    .string()
    .optional()
    .transform((val) => (val === undefined ? true : val === 'true'));

// Server-only environment variables
const serverSchema = z
  .object({
    // Backend URL for API requests (server-side only)
    TESSERA_BACKEND_URL: z.string().min(1, 'TESSERA_BACKEND_URL is required'),

    // OpenAPI configuration (server-side) - defaults to {TESSERA_BACKEND_URL}/openapi.json
    TESSERA_BACKEND_OPENAPI_URL: z.string().min(1).optional(),

    // OpenTelemetry configuration
    OTEL_EXPORTER_OTLP_ENDPOINT: z.string().min(1).optional(),

    // Feature flags (server-side only for security)
    FEATURE_CONVERSATION: booleanString(),
    FEATURE_WORKBENCH: booleanString(),
    FEATURE_EDITOR: booleanString(),
    FEATURE_CODE_REMEDY: booleanString(),
    FEATURE_DATA_FOUNDRY: booleanString(),
    FEATURE_DATA_HARMONIZATION: booleanString(),
    FEATURE_KNOWLEDGE_BASE: booleanString(),
    FEATURE_ADMIN: booleanString(),
    FEATURE_VIEW_REMEDIATION: booleanString(),
    FEATURE_RELEASES: booleanString(),

    HTTP_SECURE: z
      .enum(['true', 'false'])
      .optional()
      .transform((val) => val === 'true'),
  })
  .transform((env) => ({
    ...env,
    // Derive OpenAPI URL from backend URL if not explicitly set
    TESSERA_BACKEND_OPENAPI_URL:
      env.TESSERA_BACKEND_OPENAPI_URL ?? new URL('/openapi.json', env.TESSERA_BACKEND_URL).toString(),
  }));

// Client-safe environment variables (prefixed with VITE_)
const clientSchema = z.object({
  // Enable TanStack Query/Router devtools in the browser
  VITE_ENABLE_DEVTOOLS: z
    .enum(['true', 'false'])
    .optional()
    .transform((val) => val === 'true'),

  VITE_USER_SKILLS_BASE_PATH: z.string().optional(),
  VITE_GLOBAL_SKILLS_BASE_PATH: z.string().optional(),
});

/**
 * Validates and parses server-only environment variables.
 * This should only be called in server-side code.
 *
 * IMPORTANT: Each env var is explicitly accessed to ensure Turbo detects it.
 * This way if you add a new variable to the schema but forget to add it to turbo.json,
 * Turbo will warn you about the missing declaration.
 *
 * @throws {z.ZodError} If validation fails
 */
export function getServerEnv() {
  try {
    return serverSchema.parse({
      TESSERA_BACKEND_URL: process.env.TESSERA_BACKEND_URL,
      TESSERA_BACKEND_OPENAPI_URL: process.env.TESSERA_BACKEND_OPENAPI_URL,
      OTEL_EXPORTER_OTLP_ENDPOINT: process.env.OTEL_EXPORTER_OTLP_ENDPOINT,
      FEATURE_CONVERSATION: process.env.FEATURE_CONVERSATION,
      FEATURE_WORKBENCH: process.env.FEATURE_WORKBENCH,
      FEATURE_EDITOR: process.env.FEATURE_EDITOR,
      FEATURE_CODE_REMEDY: process.env.FEATURE_CODE_REMEDY,
      FEATURE_DATA_FOUNDRY: process.env.FEATURE_DATA_FOUNDRY,
      FEATURE_DATA_HARMONIZATION: process.env.FEATURE_DATA_HARMONIZATION,
      FEATURE_KNOWLEDGE_BASE: process.env.FEATURE_KNOWLEDGE_BASE,
      FEATURE_ADMIN: process.env.FEATURE_ADMIN,
      FEATURE_VIEW_REMEDIATION: process.env.FEATURE_VIEW_REMEDIATION,
      FEATURE_RELEASES: process.env.FEATURE_RELEASES,
      HTTP_SECURE: process.env.HTTP_SECURE,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues.map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`).join('\n');
      throw new Error(`❌ Invalid server environment variables:\n${issues}`);
    }
    throw error;
  }
}

/**
 * Validates and parses client-safe environment variables.
 * These variables are prefixed with VITE_ and are safe to expose to the browser.
 *
 * IMPORTANT: Each env var is explicitly accessed to ensure Turbo detects it.
 *
 * @throws {z.ZodError} If validation fails
 */
export function getClientEnv() {
  // import.meta.env is only available in Vite/browser context
  // In Node.js (e.g., validation scripts), this will be undefined
  if (typeof import.meta.env === 'undefined') {
    return {} as ClientEnv;
  }

  try {
    return clientSchema.parse({
      VITE_ENABLE_DEVTOOLS: import.meta.env.VITE_ENABLE_DEVTOOLS,
      VITE_USER_SKILLS_BASE_PATH: import.meta.env.VITE_USER_SKILLS_BASE_PATH,
      VITE_GLOBAL_SKILLS_BASE_PATH: import.meta.env.VITE_GLOBAL_SKILLS_BASE_PATH,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues.map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`).join('\n');
      throw new Error(`❌ Invalid client environment variables:\n${issues}`);
    }
    throw error;
  }
}

// Type exports for TypeScript autocomplete
export type ServerEnv = z.infer<typeof serverSchema>;
export type ClientEnv = z.infer<typeof clientSchema>;

/**
 * Type-safe access to server environment variables.
 * Use this instead of process.env for automatic validation and TypeScript support.
 *
 * Lazily evaluated on first access to avoid eager execution in client bundles.
 *
 * @example
 * import { serverEnv } from '@/env';
 * const backendUrl = serverEnv.TESSERA_BACKEND_URL; // ✅ Type-safe and validated
 * const openApiUrl = serverEnv.TESSERA_BACKEND_OPENAPI_URL; // ✅ Auto-derived if not set
 * const isWorkbenchEnabled = serverEnv.FEATURE_WORKBENCH; // ✅ Boolean, not string
 */
export const serverEnv: ServerEnv = new Proxy({} as ServerEnv, {
  get(_target, prop) {
    if (serverEnvCache === undefined) {
      serverEnvCache = getServerEnv();
    }
    return serverEnvCache[prop as keyof ServerEnv];
  },
});

let serverEnvCache: ServerEnv | undefined = undefined;

/**
 * Type-safe access to client environment variables.
 * Use this instead of import.meta.env for automatic validation and TypeScript support.
 *
 * @example
 * import { clientEnv } from '@/env';
 * const showDevtools = clientEnv.VITE_ENABLE_DEVTOOLS; // ✅ Boolean, validated
 *
 * Note: Use import.meta.env directly for Vite built-ins like DEV, MODE, PROD, etc.
 */
export const clientEnv = /* @__PURE__ */ getClientEnv();
